import React from "react";
import type { IExamReport } from "@/apis/exam_papers/getExamReport";
import type { ICategory } from "@/apis/categories/types";
import ProgressBar from "./ProgressBar";
import { QuestionTable } from "@/components/exam/QuestionTable";
import { CategoryStatsTable } from "@/components/exam/CategoryStatsTable";
import { Badge } from "@/components/badge";
import { FEATURES } from "@/config/features";
import { getPaperTypeLabel } from "@/constants/exam";

interface ACTScoreReportProps {
  examReport: IExamReport;
  categories?: ICategory[];
}

const ACTScoreReport: React.FC<ACTScoreReportProps> = ({
  examReport,
  categories,
}) => {
  const isDevelopment = false; //process.env.NODE_ENV === "development";

  const calculateDomainPercentage = (
    domainQuestions: number,
    allDomains: Array<{ total_questions?: number }>
  ) => {
    const totalQuestions = allDomains.reduce(
      (sum, d) => sum + (d.total_questions || 0),
      0
    );
    return Math.round((domainQuestions / (totalQuestions || 1)) * 100);
  };

  const { subjects } = examReport;
  if (!subjects || subjects.length === 0) {
    return <div>No subject details found</div>;
  }

  // Get composite score from API
  const compositeScore = examReport?.total_score || 0;

  return (
    <>
      {/* PAGE 1: ACT Practice Score Report */}
      <div
        className={`page w-[210mm] h-[297mm] px-8 py-6 text-black font-sans text-base bg-white ${
          isDevelopment ? "border-2 border-red-500" : ""
        }`}
      >
        {/* Header */}
        <div className="flex justify-between items-start mb-2 h-[100px]">
          <div className="flex items-center gap-3">
            <span className="text-3xl font-bold tracking-tight ">
              S&S ACADEMY
            </span>
          </div>
          <div className="text-right ">
            <div className="inline-block rounded-lg p-2 text-left">
              <div className="text-lg mb-1 leading-none">
                <span className="inline-block w-24">Name:</span>
                <span className="font-bold">{examReport?.user?.name}</span>
              </div>
              {examReport?.group?.name && (
                <div className="text-base mb-1 leading-none">
                  <span className="inline-block w-24">Class:</span>
                  <span className="font-bold">{examReport?.group?.name}</span>
                </div>
              )}
              {examReport?.user?.school && (
                <div className="text-base mb-1 leading-none">
                  <span className="inline-block w-24">School:</span>
                  <span className="font-bold">{examReport?.user?.school}</span>
                </div>
              )}
              {examReport?.user?.grade && (
                <div className="text-base mb-1 leading-none">
                  <span className="inline-block w-24">Grade:</span>
                  <span className="font-bold">{examReport?.user?.grade}</span>
                </div>
              )}
              <div className="text-base mb-1 leading-none">
                <span className="inline-block w-24">Email:</span>
                <span className="font-bold">{examReport?.user?.email}</span>
              </div>
              <div className="text-base leading-none">
                <span className="inline-block w-24">Tested on:</span>
                <span className="font-bold">
                  {new Date(examReport?.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Title */}
        <h1 className="text-4xl font-extrabold mt-16 mb-6">
          Your ACT Score Report
        </h1>

        {/* Card Section */}
        <div className="rounded-xl border border-gray-300 bg-white shadow-sm mt-2 mb-4 p-0 overflow-hidden">
          <div className="flex justify-between items-center px-6 py-3 border-b border-gray-200 bg-gray-200">
            <div className="text-xl font-bold">
              {examReport?.exam_set?.title}
            </div>
            <div className="text-base text-gray-700">
              {new Date(examReport?.created_at).toLocaleDateString()}
            </div>
          </div>
          <div className="grid grid-cols-20 gap-0 divide-x divide-gray-200">
            {/* Composite Score */}
            <div className="col-span-5 p-4 px-6 flex flex-col items-start">
              <div className="text-md font-extrabold">COMPOSITE SCORE</div>
              <div className="text-6xl font-bold leading-none mb-2">
                {compositeScore}
              </div>
              <div className="text-sm text-gray-600 mb-2">
                Score Range
                <br /> 1-36
              </div>
              <div className="text-sm text-gray-800 font-bold mt-2">
                Score Range:
              </div>
              <div className="text-sm text-gray-600">
                This range reflects the minimum and maximum composite score you can
                get with this assessment.
              </div>
            </div>
            {/* Subject Scores */}
            <div className="col-span-6 p-4 flex flex-col items-start">
              <div className="text-md font-extrabold mb-3">SUBJECT SCORES</div>
              <div className="grid grid-cols-2 gap-4 w-full h-full">
                {subjects.map((subjectReport) => (
                  <div key={subjectReport.subject} className="flex flex-col">
                    <div className="font-bold text-sm mb-2">
                      {getPaperTypeLabel(subjectReport.subject)}
                    </div>
                    <div className="font-bold text-4xl leading-none">
                      {subjectReport.total_score || 0}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Score Details */}
            <div className="col-span-9 py-4 px-4 flex flex-col items-start">
              <div className="text-md font-extrabold mb-3">SCORE DETAILS</div>
              <div className="grid grid-cols-2 gap-4 w-full text-sm">
                {subjects.map((subjectReport) => (
                  <div key={subjectReport.subject}>
                    <p className="text-sm font-bold mb-2">
                      {getPaperTypeLabel(subjectReport.subject)}
                    </p>
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-2xl font-bold">
                        {subjectReport.sections[0]?.questions?.filter(
                          (q) => q.is_correct
                        ).length || 0}
                      </p>
                      <div className="border-l border-gray-300 h-4"></div>
                      <p className="text-xs leading-none font-bold">
                        correct<br />answers
                      </p>
                    </div>
                    <p className="text-xs">
                      Total: {subjectReport.sections[0]?.questions?.length || 0}
                    </p>
                    <p className="text-xs">
                      Incorrect: {subjectReport.sections[0]?.questions?.filter(
                        (q) => !q.is_correct
                      ).length || 0}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Knowledge and Skills */}
        <div className="mt-8">
          <div className="font-bold text-xl mb-1">KNOWLEDGE AND SKILLS</div>
          <div className="text-sm text-gray-700 mb-6">
            View your performance across the content domains measured on the ACT
          </div>
          <div className="grid grid-cols-2 gap-6">
            {subjects.map((subjectReport) =>
              subjectReport.domains && subjectReport.domains.length > 0 ? (
                <div key={subjectReport.subject}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="text-black font-semibold text-lg">
                      {getPaperTypeLabel(subjectReport.subject)}
                    </div>
                    {FEATURES.SHOW_REPORT_DIFFICULTY_LEVEL &&
                      subjectReport.difficulty && (
                        <Badge color="zinc">
                          Level {subjectReport.difficulty}
                        </Badge>
                      )}
                  </div>
                  {subjectReport.domains.map((domain) => (
                    <div key={domain?.id}>
                      <div className="mb-2 text-sm text-gray-700 mt-2">
                        <p className="font-bold">{domain.name}</p>
                        <p className="text-gray-600">
                          (
                          {calculateDomainPercentage(
                            domain?.total_questions || 0,
                            subjectReport.domains
                          )}
                          % of test section, {domain?.total_questions || 0}{" "}
                          questions)
                        </p>
                      </div>
                      <ProgressBar percentage={(domain?.accuracy || 0) * 100} />
                    </div>
                  ))}
                </div>
              ) : null
            )}
          </div>
        </div>
      </div>

      {/* Question Breakdown Pages - one page per subject */}
      {subjects.map((subjectReport) => (
        <div
          key={subjectReport.subject}
          className={`page w-[210mm] h-[297mm] px-10 text-black font-sans text-sm bg-white ${
            isDevelopment ? "border-2 border-red-500" : ""
          }`}
          style={{ pageBreakBefore: "always" }}
        >
          {/* Questions - 2 columns */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <QuestionTable
                title={getPaperTypeLabel(subjectReport.subject)}
                questions={subjectReport.sections[0]?.questions?.slice(0, 60)}
                showAccuracy={true}
                progressId={examReport?.exam_progress_id}
              />
            </div>
            {subjectReport.sections[0]?.questions?.slice(60)?.length > 0 && (
              <div>
                <QuestionTable
                  title={getPaperTypeLabel(subjectReport.subject)}
                  questions={subjectReport.sections[0]?.questions?.slice(60)}
                  showAccuracy={true}
                  startNumber={61}
                  progressId={examReport?.exam_progress_id}
                />
              </div>
            )}
          </div>

          {/* Category Stats */}
          <div className="mt-4">
            <CategoryStatsTable
              title="QUESTION COUNTS BY DETAILED CATEGORIES"
              examType={examReport?.exam_set?.exam_type || "ACT"}
              subject={subjectReport.subject}
              categoryStats={subjectReport.category_stats}
              recentExamsStats={subjectReport.recent_exams_stats}
            />
          </div>
        </div>
      ))}
    </>
  );
};

export default ACTScoreReport;
